<template>
  <main>
    <v-toolbar style="background-color: #444; color: #fff; border-radius: 5px">
      <v-toolbar-title>{{ formTitle }}</v-toolbar-title>
    </v-toolbar>
    <v-form @submit.prevent="handleSubmit">
      <formWrapper :validator="$v.form">
        <v-container>
          <v-row>
            <v-col cols="12">
              <locale-select v-if="isEdit" v-model="locale" @change="changeLocale" />
            </v-col>
            <v-col cols="12">
              <span>{{ $t("labels.main_photo") }}</span><span style="color: #f00">*</span>
              <section class="file-upload">
                <new-image-upload class="file-upload__image" :imgUrl="form.main_media ? form.main_media.path : ''"
                  :imgId="form.main_media ? form.main_media.id : ''" @fileSelected="onFileChange" :checkRatio="false"
                  :maxSize="maxSize" />
              </section>
            </v-col>
            <v-col cols="12">
              <span>{{ $t("labels.media") }}</span>
              <section class="file-upload__gallery pa-0">
                <multi-image-upload class="file-upload__gallery--upload" :maxSize="maxSize"
                  @fileSelected="handleMultiPhotos" @handle_delete_image="onDeleteImage" :imgsUrl="imgsUrl" />
              </section>
            </v-col>
            <v-col cols="12">
              <first-step :countries="countries" :tourismCities="tourismCities" :isEdit="isEdit" :form="form"
                :categories="categories" :weekDays="weekDays" :tags="tags" />
            </v-col>

            <v-col cols="12">
              <CustomEditor :placeholder="$t('fields.description')" class="editor" :form="form" fieldName="description" />
            </v-col>
            <v-col cols="12" v-if="isEdit">
              <second-step :isEdit="isEdit" :form="form" :program="program" :locale="locale" />
            </v-col>
            <v-col cols="12">
              <v-btn :disabled="$v.form.$invalid" :loading="loadingButton" type="submit" class="primary" large block
                rounded>{{ $t("button.save") }}
                <v-icon>mdi-content-save</v-icon></v-btn>
            </v-col>
            <!-- :disabled="$v.form.$invalid" -->
          </v-row>
        </v-container>
      </formWrapper>
    </v-form>
  </main>
</template>

<script>
import { VueEditor } from "vue2-editor";
import {
  required,
  minLength,
  maxLength,
  minValue,
  maxValue,
  requiredIf
} from "vuelidate/lib/validators";
import { maxWords } from "@/utils/validate";
import {
  IndexData,
  StoreData,
  ShowData,
  UpdateData
} from "@/helpers/apiMethods";

export default {
  name: "CreateAndEditTrip",
  components: {
    FirstStep: () => import("./first_step"),
    SecondStep: () => import("./second_step.vue"),
    VueEditor
  },
  provide() {
    return {
      $v: this.$v
    };
  },
  data() {
    return {
      customToolbar: [
        ["bold", "italic", "underline", "strike"],
        [{ align: "" }, { align: "justify" }, { align: "right" }],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ direction: "rtl" }]
      ],
      maxSize: 1.24,
      loadingButton: false,
      locale: this.locale,
      program: {},
      form: {
        country: "",
        tourism_city: "",
        name: "",
        sub_name: "",
        description: "",
        label: "",
        duration_in_hours: "",
        cancel_before_start_in_hours: "",
        reserve_before_start_in_hours: "",
        person_price: "",
        child_price: "",
        return_pct: "",
        week_days: [],
        categories: [],
        tags: [],
        excludes: [],
        includes: [],
        needs: [],
        conditions: [],
        available_dates: [],
        main_media: "",
        auto_accept_order: "0",
        allow_custom_dates: "0",
        available: "0",
        visible: "0"
      },
      tags: [],
      media: [],
      categories: [],
      countries: [],
      imgsUrl: [],
      tourismCities: [],
      weekDays: []
    };
  },
  computed: {
    formTitle() {
      if (this.$route.name === "create-trip") {
        return this.$t("heading.create_trip");
      } else {
        return this.$t("heading.edit_trip");
      }
    },
    isEdit() {
      if (this.$route.name === "create-trip") {
        return false;
      } else {
        return true;
      }
    }
  },
  mounted() {
    this.getCountries();

    this.getTags();
    this.getTourismCities();
    this.getWeekDays();
    this.getCategories();
  },
  methods: {
    onFileChange({ file }) {
      this.form.main_media = file;
    },
    async getTourismCities() {
      const res = await IndexData({
        reqName: "tourism-cities",
        query: { pagination: "all" }
      });
      const { tourism_cities } = res.data;
      this.tourismCities = tourism_cities;
    },
    async getWeekDays() {
      const res = await IndexData({
        reqName: "week-days",
        query: { pagination: "all" }
      });
      const { week_days } = res.data;
      this.weekDays = week_days;
    },
    async getCategories() {
      const res = await IndexData({
        reqName: "trip-categories?visible=yes",
        query: { pagination: "all" }
      });
      const { trip_categories } = res.data;
      this.categories = trip_categories;
    },
    async getTags() {
      const res = await IndexData({ reqName: "tags", pagination: "all" });
      const { tags } = res.data;
      this.tags = tags;
    },

    async getCountries() {
      const res = await IndexData({
        reqName: "countries",
        query: { pagination: "all", simple: true }
      });
      const { countries } = res.data;
      this.countries = countries;
    },

    handleShowData(locale = this.locale) {
      ShowData({
        reqName: "trips",
        param: this.$route.params.slug,
        locale
      }).then(res => {
        const { trip } = res.data;
        this.imgsUrl = trip.media.filter(el => !el.main);
        this.media = [...this.imgsUrl];
        this.program = trip.daily_program;
        this.form = {
          name: trip.name,
          description: trip.description,
          tourism_city: trip.tourism_city,
          country: trip.country.id,
          main_media: trip.main_media,
          label: trip.label,
          person_price: trip.person_price,
          child_price: trip.child_price,
          cancel_before_start_in_hours: trip.cancel_before_start_in_hours,
          reserve_before_start_in_hours: trip.reserve_before_start_in_hours,
          return_pct: trip.return_pct,
          sub_name: trip.sub_name,
          duration_in_hours: trip.duration_in_hours,
          allow_custom_dates: trip.allow_custom_dates ? "1" : "0",
          auto_accept_order: trip.auto_accept_order ? "1" : "0",
          premium: trip.premium ? "1" : "0",
          available: trip.available ? "1" : "0",
          visible: trip.visible ? "1" : "0",
          categories: trip.categories.map(category => category.id),
          week_days: trip.week_days.map(week_day => week_day.id),
          tags: trip.tags.map(tag => tag.id),
          needs: trip.needs.map(need => need.id),
          excludes: trip.excludes.map(exclude => exclude.id),
          conditions: trip.conditions.map(condition => condition.id),
          includes: trip.includes.map(include => include.id),
          available_dates: trip.available_dates.map(available_date =>
            this.$moment(available_date.available_at).format("YYYY-MM-DD")
          )
        };
      });
    },
    generateData() {
      const formData = new FormData();
      const exclude = [
        "week_days",
        "categories",
        "tags",
        "excludes",
        "includes",
        "conditions",
        "available_dates",
        "needs"
      ];

      if (this.isEdit) {
        delete this.form.main_media;
      }
      if (this.media && this.media.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.media.forEach((media) => {
          if (!media.id) formData.append("media[]", media);
        });
      }
      if (this.form.categories && this.form.categories.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.categories.forEach((category) => {
          formData.append(`categories[]`, category);
        });
      }
      if (this.form.includes && this.form.includes.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.includes.forEach((include) => {
          formData.append(`includes[]`, JSON.stringify(include));
        });
      } else {
        formData.append(`includes`, "''");
      }
      if (this.form.excludes && this.form.excludes.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.excludes.forEach((exclude) => {
          formData.append(`excludes[]`, exclude);
        });
      } else {
        formData.append(`excludes`, "''");
      }
      if (this.form.needs && this.form.needs.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.needs.forEach((need) => {
          formData.append(`needs[]`, need);
        });
      } else {
        formData.append(`needs`, "''");
      }
      if (this.form.conditions && this.form.conditions.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.conditions.forEach((condition) => {
          formData.append(`conditions[]`, condition);
        });
      } else {
        formData.append(`conditions`, "''");
      }
      if (this.form.available_dates && this.form.available_dates.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.available_dates.forEach((available_date) => {
          formData.append(`available_dates[]`, `${available_date} 00:00:00`);
        });
      } else {
        formData.append(`available_dates`, "''");
      }

      if (this.form.tags && this.form.tags.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.tags.forEach((tag, index) => {
          formData.append(`tags[${index}]`, tag);
        });
      } else {
        formData.append(`tags`, "''");
      }
      if (this.form.week_days && this.form.week_days.length > 0) {
        // eslint-disable-next-line prettier/prettier
        this.form.week_days.forEach((week_day, index) => {
          formData.append(`week_days[${index}]`, week_day);
        });
      } else {
        delete this.form.week_days;
      }

      for (const key in this.form) {
        if (!exclude.includes(key)) {
          if (this.form[key] !== null && this.form[key] !== "null") {
            formData.append(key, this.form[key]);
          }
        }
      }
      formData.append("label", this.form.label ? this.form["label"] : "");
      if (this.isEdit) {
        formData.append("_method", "put");
        formData.append("locale", this.locale);
      }
      return formData;
    },
    onCreate() {
      StoreData({ reqName: "trips", data: this.generateData() })
        .then(() => {
          this.$router.push("/trips");
        })
        .finally(() => {
          this.loadingButton = false;
        });
    },
    onUpdate() {
      UpdateData({
        reqName: "trips",
        data: this.generateData(),
        param: this.$route.params.slug,
        locale: this.locale
      })
        .then(() => {
          this.$router.push("/trips");
        })
        .finally(() => {
          this.loadingButton = false;
        });
    },
    handleSubmit() {
      this.loadingButton = true;

      if (!this.isEdit) {
        // in case create
        this.onCreate();
      } else {
        // in case edit
        this.onUpdate();
      }
    },
    changeLocale(locale) {
      this.locale = locale;
      this.handleShowData(locale);
    },

    handleMultiPhotos(files) {
      if (!this.media) {
        this.media = [];
      }
      this.media = [...this.media, ...files];
    },
    onDeleteImage(index) {
      this.media.splice(index);
    }
  },
  validations: {
    form: {
      country: {
        required
      },
      tourism_city: {
        required
      },
      name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(191)
      },
      sub_name: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(191)
      },
      // description: {
      //   maxWords: maxWords(40000)
      // },
      label: {
        maxLength: maxLength(16)
      },
      duration_in_hours: {
        required,
        minValue: minValue(1),
        maxValue: maxValue(24)
      },
      cancel_before_start_in_hours: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(336)
      },
      reserve_before_start_in_hours: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(336)
      },
      person_price: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(500000)
      },
      child_price: {
        required,
        minValue: minValue(0),
        maxValue: maxValue(500000)
      },
      return_pct: {
        minValue: minValue(0),
        maxValue: maxValue(100)
      },
      week_days: {
        required
      },
      main_media: {
        required
      }
    }
  },

  watch: {
    isEdit: {
      handler(value) {
        if (value) {
          this.handleShowData();
        }
      },
      immediate: true
    }
  }
};
</script>
